<template>
  <div class="special-commision-offer-card">
    <div class="product-area">
      <div class="image-wrapper">
        <img :src="offer.productImage" :alt="offer.productName" />
      </div>
      <div class="content-area">
        <p class="title">{{ offer.productName }}</p>
        <div class="price-selection">
          <p class="label">{{ cardDisplayOffer.offerTitle }}</p>
          <p class="price-range">{{ cardDisplayOffer.priceRangeText }}</p>
          <span class="commission-badge">{{ cardDisplayOffer.commissionText }}</span>
        </div>
      </div>
    </div>
    <div class="offer-popover-wrapper">
      <Popover
        ref="popoverRef"
        position="bottom-center"
        color="#ffffff"
        :withOverlay="true"
        :zIndex="10001"
        to="body"
      >
        <template v-slot:content="{ on }">
          <button
            type="button"
            class="offer-button"
            v-on="on"
            @click="resetOfferPopup"
          >
            Teklif Seç
          </button>
        </template>

        <template #popoverContent>
          <div class="offer-popover-wrapper__content">
            <div class="offer-popover-wrapper__content__header">
              <p class="offer-popover-wrapper__content__title">Teklif Seçin</p>
              <p v-if="offer.hasVariants" class="all-variants-badge">Tüm Varyantlarda Geçerli</p>
              <p v-else class="all-variants-badge">Tek Varyantta Geçerli</p>
            </div>
            <div class="offer-popover-wrapper__content__product">
              <div class="offer-popover-wrapper__content__product-image">
                <img :src="offer.productImage" :alt="offer.productName" />
              </div>
              <p class="offer-popover-wrapper__content__product-name">{{ offer.productName }}</p>
            </div>
            <div class="offer-popover-wrapper__current">
              <p class="current-title">Mevcut satış fiyatı ve komisyon:</p>
              <div class="current-details">
                <p class="current-details__price">{{ offer.displayPriceText }}</p>
                <p class="current-details__text">{{ offer.commissionRateText }}</p>
              </div>
            </div>
            <div class="offer-popover-wrapper__list">
              <div
                v-for="offerKey in offerKeys"
                :key="offerKey"
                class="offer-item"
              >
                <RadioButton
                  name="offer"
                  :preSelected="selectedOffer === offerKey"
                  @select="selectOffer(offerKey)"
                  :isInputFull="true"
                >
                  <div class="offer-item__content">
                    <span class="offer-item__title">{{ getOfferTitle(offerKey) }}</span>
                    <div class="current-values">
                      <p class="current-values__price">
                        {{ getOfferListItem(offerKey).priceRangeText }}
                      </p>
                      <p class="commission">{{ getOfferListItem(offerKey).commissionText }}</p>
                    </div>
                  </div>
                </RadioButton>
              </div>
            </div>
            <CurrencyInput
              ref="currencyInput"
              v-model="newSalesPrice"
              class="offer-popover-wrapper__content__input"
              :options="currencyInputOptions"
              :class="[{ error: errorText }]"
              :key="`currency-input-${selectedOffer}`"
            />
            <p v-if="errorText" class="offer-popover-wrapper__content__error-text">{{ errorText }}</p>
            <AppButton
              title="Devam Et"
              md
              primary
              @click="handleContinue"
            />
          </div>
        </template>
      </Popover>
    </div>
  </div>
</template>

<script>
import { Popover, AppButton, RadioButton } from '@n11/so-ui-components';
import CurrencyInput from '@/components/DataTable/CurrencyInput';
import { sendProductDealSelectEvent, sendProductDealPopUpViewEvent, sendProductDealApplyEvent, normalizeGa4Price } from '@/utils/ga4/helper';
import { formatDashboardDate } from '@/utils/formatedDate';

const OFFER_KEYS = ['advantageousOffer', 'superOffer', 'shockingOffer'];

const OFFER_TYPE_BY_KEY = {
  advantageousOffer: 'ADVANTAGEOUS_PRICE',
  superOffer: 'SUPER_PRICE',
  shockingOffer: 'SHOCKING_PRICE',
};

export default {
  name: 'SpecialCommisionOfferCard',
  components: {
    Popover,
    AppButton,
    RadioButton,
    CurrencyInput,
  },
  props: {
    offer: {
      type: Object,
      required: true,
    },
  },
  emits: ['continue'],
  computed: {
    cardDisplayOffer() {
      const shockingOffer = this.offer.offers?.find(
        (item) => item.offerType === 'SHOCKING_PRICE',
      );
      return shockingOffer || this.offer.offers?.[this.offer.offers.length - 1] || {};
    },
    offerKeys() {
      return OFFER_KEYS.filter((key) => this.offer[key]);
    },
  },
  data() {
    return {
      selectedOffer: '',
      newSalesPrice: null,
      errorText: '',
      currencyInputOptions: {
        locale: 'tr-TR',
        currency: 'TRY',
        precision: 2,
        currencyDisplay: 'symbol',
        hideCurrencySymbolOnFocus: false,
        hideGroupingSeparatorOnFocus: false,
        hideNegligibleDecimalDigitsOnFocus: false,
        autoDecimalDigits: false,
        useGrouping: true,
        accountingSign: false,
        valueRange: { min: 0 },
      },
    };
  },
  methods: {
    getOfferListItem(offerKey) {
      const offerType = OFFER_TYPE_BY_KEY[offerKey];
      return this.offer.offers?.find((item) => item.offerType === offerType) || {};
    },
    getOfferTitle(offerKey) {
      return this.getOfferListItem(offerKey).offerTitle || offerKey;
    },
    formatPrice(price) {
      return price.toLocaleString('tr-TR', {
        style: 'currency',
        currency: 'TRY',
      });
    },
    selectOffer(offerKey) {
      const rates = this.offer[offerKey];
      if (!rates) return;

      this.selectedOffer = offerKey;
      this.newSalesPrice = offerKey === 'shockingOffer' ? rates.upperPrice : rates.lowerPrice;
      this.$refs.currencyInput?.setValue(this.newSalesPrice);
    },
    getOfferGa4Params(user) {
      return {
        current_deal_label: 'Mevcut Fiyat & Komisyon',
        current_price: normalizeGa4Price(this.offer.displayPrice),
        current_commission: String(this.offer.commissionRate ?? ''),
        item_name: this.offer.productName ?? '',
        item_stock: String(this.offer.stockCount ?? ''),
        item_id: String(this.offer.productId ?? ''),
        item_category_id: String(this.offer.categoryId ?? ''),
        deal_start_date: this.offer.startDate ? formatDashboardDate(this.offer.startDate) : '',
        deal_end_date: this.offer.finishDate ? formatDashboardDate(this.offer.finishDate) : '',
        deal_id: String(this.offer.dealId ?? ''),
        seller_name: user?.username ?? '',
        seller_id: String(user?.sellerId ?? ''),
      };
    },
    resetOfferPopup() {
      const user = this.$store.getters['auth/user'];
      const displayOffer = this.cardDisplayOffer;
      const shockingOffer = this.offer.shockingOffer || {};
      const commonParams = this.getOfferGa4Params(user);
      sendProductDealSelectEvent({
        ...commonParams,
        page_type: 'Homepage',
        button_name: 'Teklif Seç',
        new_deal_label: 'Yeni Fiyat & Komisyon',
        new_deal_name: displayOffer.offerTitle ?? '',
        new_price: normalizeGa4Price(
          `${shockingOffer.upperPrice ?? 0}-${shockingOffer.lowerPrice ?? 0}`,
        ),
        new_commission: String(shockingOffer.commission ?? ''),
      });
      sendProductDealPopUpViewEvent({
        ...commonParams,
        page_type: 'Homepage',
      });
      this.newSalesPrice = null;
      this.selectedOffer = '';
      this.errorText = '';
    },
    handleContinue() {
      const offerKey = this.selectedOffer;
      if (!offerKey) {
        this.errorText = 'Bir teklif seçmelisiniz.';
        return;
      }

      const { upperPrice, lowerPrice, commission } = this.offer[offerKey];
      if (this.newSalesPrice > upperPrice) {
        this.errorText = `Bu teklif için fiyat en fazla ₺${upperPrice} olmalıdır.`;
      } else if (this.newSalesPrice < lowerPrice) {
        this.errorText = `Bu teklif için fiyat en az ₺${lowerPrice} olmalıdır.`;
      } else {
        this.errorText = '';
        const user = this.$store.getters['auth/user'];
        const selectedOfferItem = this.getOfferListItem(offerKey);
        const offerEventParams = {
          ...this.getOfferGa4Params(user),
          page_type: 'Ürün Komisyon Teklifleri',
          button_name: 'Devam Et',
          new_deal_label: 'Yeni Fiyat & Komisyon',
          new_deal_name: selectedOfferItem.offerTitle ?? '',
          new_price: normalizeGa4Price(this.newSalesPrice),
          new_commission: String(commission ?? ''),
        };
        sendProductDealApplyEvent(offerEventParams);
        if (this.$refs.popoverRef?.popoverToogle) {
          this.$refs.popoverRef.popoverToogle();
        }
        this.$emit('continue', {
          offerKey,
          price: this.newSalesPrice,
          commission,
          viewPromotionParams: offerEventParams,
        });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/assets/styles/variables.scss';

.special-commision-offer-card {
  width: 350px;
  border: 0.5px solid $colorN40;
  border-radius: 8px;
  background: $colorN00;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 0 0 350px;
}

.product-area {
  display: flex;
  align-items: flex-start;
  gap: 8px;
}

.image-wrapper {
  width: 117px;
  height: 130px;
  border-radius: 6px;
  overflow: hidden;
  flex-shrink: 0;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
}

.content-area {
  min-width: 0;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.title {
  margin: 0;
  color: $colorN80;
  font-size: 14px;
  font-weight: 600;
  line-height: 18px;
  letter-spacing: -0.05px;
  height: 54px;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.price-selection {
  height: 72px;
  border: 1px solid $colorN40;
  border-radius: 8px;
  background: $colorN00;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 5px 9px;
}

.label {
  margin: 0;
  color: $colorN80;
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
}

.price-range {
  margin: 0;
  color: $colorN80;
  font-size: 12px;
  font-weight: 600;
  line-height: 16px;
}

.commission-badge {
  border: 1px solid $colorA50;
  border-radius: 4px;
  background: $colorA05;
  padding: 3px 8px;
  color: $colorN80;
  font-size: 11px;
  font-weight: 600;
  line-height: 14px;
}

.offer-button {
  width: 100%;
  border: 0;
  border-radius: 6px;
  background: $colorN80;
  color: $colorN00;
  font-size: 13px;
  font-weight: 700;
  line-height: 18px;
  letter-spacing: -0.05px;
  padding: 7px 16px;
  cursor: pointer;
}

.offer-popover-wrapper {
  .popover {
    width: 100%;
  }

  &__content {
    border-radius: 8px;
    border: 1px solid $colorD40;
    box-shadow: 0 0 0 3px $colorD20;
    display: flex;
    flex-direction: column;
    gap: 8px;
    width: 350px;
    padding: 12px;
    background-color: $colorN00;

    &__header {
      display: flex;
      justify-content: space-between;
      align-items: center;
    }

    &__product {
      display: flex;
      align-items: flex-start;
      gap: 8px;

      &-image {
        width: 58px;
        height: 64px;
        flex-shrink: 0;
        border-radius: 6px;
        overflow: hidden;

        img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
        }
      }

      &-name {
        margin: 0;
        min-width: 0;
        flex: 1;
        color: $colorN80;
        font-size: 12px;
        font-weight: 600;
        line-height: 16px;
        letter-spacing: -0.05px;
        text-align: left;
        display: -webkit-box;
        -webkit-line-clamp: 3;
        -webkit-box-orient: vertical;
        overflow: hidden;
      }
    }

    &__title {
      color: $colorN80;
      font-size: 13px;
      font-weight: 700;
      line-height: 18px;
      text-align: start;
      letter-spacing: -0.05px;
      margin: 0;
    }

    &__error-text {
      color: $colorE50;
      font-size: 11px;
      font-weight: 400;
      line-height: 14px;
      text-align: left;
      margin-top: -4px;
    }

    &__input {
      &.error {
        border: 1px solid $colorE50;
        outline: 3px solid $colorE10;
      }
    }
  }

  &__current {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;

    .current-title {
      color: $colorN60;
      font-size: 11px;
      line-height: 14px;
      text-align: left;
      font-weight: 400;
    }

    .current-details {
      display: flex;
      align-items: center;
      gap: 4px;

      &__price {
        color: $colorN80;
        font-size: 13px;
        font-weight: 700;
        line-height: 18px;
        margin: 0;
      }

      &__text {
        color: $colorN60;
        font-size: 11px;
        line-height: 14px;
        font-weight: 600;
        padding: 2px 6px;
        border-radius: 4px;
        border: 1px solid $colorN30;
        background: $colorN00;
        margin: 0;
      }
    }
  }

  &__list {
    display: flex;
    flex-direction: column;
    gap: 8px;

    :deep(.radioButton-container) {
      width: 100%;

      & input:checked + .radioButton {
        border-color: $colorN80;
      }

      & input:checked + .radioButton .radioButton-inner {
        background: $colorN80;
      }

      .radioButton {
        flex-shrink: 0;
      }
    }

    .offer-item {
      &__content {
        display: flex;
        flex-direction: column;
        text-align: left;
        width: 100%;
      }

      &__title {
        color: $colorN60;
        font-size: 11px;
        line-height: 14px;
        font-weight: 400;
      }
    }

    .current-values {
      display: flex;
      flex-direction: row;
      align-items: center;
      gap: 4px;
      margin-top: 1px;

      &__price {
        color: $colorN80;
        font-size: 12px;
        line-height: 16px;
        font-weight: 400;
        margin: 0;
      }

      .commission {
        flex-shrink: 0;
        font-size: 11px;
        font-weight: 600;
        color: $colorN60;
        padding: 2px 6px;
        border: 1px solid $colorN30;
        border-radius: 4px;
        background-color: $colorN00;
        line-height: 14px;
        margin: 0;
      }
    }
  }
}

.all-variants-badge {
  color: $colorN80;
  font-size: 10px;
  font-weight: 600;
  line-height: 12px;
  padding: 3px 6px;
  border: 1px solid $colorN40;
  border-radius: 4px;
  background-color: $colorN00;
  white-space: nowrap;
  width: fit-content;
  margin: 0;
}
</style>

<style lang="scss">
.popover-text.is-teleported {
  padding: 0 !important;
}
</style>
